import {
  DEFAULT_PRODUCT,
  PRODUCT_KEYS,
  PRODUCT_LANDING_PATH,
  type ProductKey,
} from "../../../shared/products/keys";
import { isSharedPath } from "../../routes/sharedPaths";

/*
 * The active product, as plain functions (no store, no router), so the route guard's rules, the
 * activity tracker and the nav config can import it without the hooks' dependencies. The hooks
 * are in hooks/useActiveProduct.ts.
 */

/**
 * The product whose ROUTES `pathname` belongs to (spec §2): the non-default product whose landing
 * path is a path segment prefix of it, else hire. This is about routing (the route guard's rules,
 * activity tracking), not about which shell to draw: a shared page such as Users is a hire route
 * that either shell may show. For the shell, use useActiveProduct.
 */
export function activeProductFor(pathname: string): ProductKey {
  let best: ProductKey = DEFAULT_PRODUCT;
  let bestLength = 0;
  for (const key of PRODUCT_KEYS) {
    const landing = PRODUCT_LANDING_PATH[key];
    if (landing === "/") continue;
    if (
      (pathname === landing || pathname.startsWith(`${landing}/`)) &&
      landing.length > bestLength
    ) {
      best = key;
      bestLength = landing.length;
    }
  }
  return best;
}

/**
 * The product a path makes active, or null for a shared page (SHARED_PATHS, and the landing `/`),
 * which keeps whatever product the user is in. skyAdvisor's PackRouteGuard rule: only a product's
 * own pages choose the product.
 */
export function productForcedBy(pathname: string): ProductKey | null {
  return isSharedPath(pathname) ? null : activeProductFor(pathname);
}

/**
 * Where a user with no valid stored choice is: hire when they hold it, else the first product they
 * hold; holding none, hire unless the instance lacks it (a performance-only instance, where a
 * platform admin on the staff Products page keeps that instance's shell). A single-product user's
 * active product is therefore always that product.
 */
export function defaultProductFor(
  held: readonly ProductKey[],
  enabled: readonly ProductKey[],
): ProductKey {
  if (held.includes(DEFAULT_PRODUCT)) return DEFAULT_PRODUCT;
  if (held.length > 0) return held[0];
  if (enabled.length === 0 || enabled.includes(DEFAULT_PRODUCT)) return DEFAULT_PRODUCT;
  return enabled[0];
}

export interface ActiveProductInput {
  pathname: string;
  /** The stored choice for this instance (lastProduct), unvalidated. */
  stored: ProductKey | null;
  /** Products have resolved (useEnabledProducts().ready). */
  ready: boolean;
  /** The products the instance has. */
  enabled: readonly ProductKey[];
  /** The products the instance has AND the user holds a role in, in PRODUCT_KEYS order. */
  held: readonly ProductKey[];
}

export interface ActiveProduct {
  /** The product whose shell is drawn: sidebar, wordmark, the switcher's pressed tab. */
  product: ProductKey;
  /**
   * Nothing is decided yet: a non-default product's page, or a shared page with a stored
   * non-default product, before products resolve. The sidebar draws nothing rather than flash hire.
   */
  pending: boolean;
  /** The product this path forces, or null on a shared page. */
  forced: ProductKey | null;
  /** The stored choice when it is still valid (enabled and held), else null. */
  storedValid: ProductKey | null;
  /** As ActiveProductInput.held. */
  held: readonly ProductKey[];
}

/**
 * The active product (the skyAdvisor PackContext pattern): a stored, validated choice.
 *
 * - A product's own page forces it (`/performance/**` performance; any other non-shared page hire).
 *   This is exactly the URL rule that held before, so a page that is not shared draws what it did.
 * - A shared page (and `/`) keeps the stored product, used only if the instance still has it and
 *   the user still holds a role in it; otherwise defaultProductFor. Never access: it picks between
 *   products the user already holds, and the route guard and the server decide what opens.
 * - Before products resolve nothing is decided on a shared page holding a stored non-default
 *   product (pending). With nothing like that stored it is hire, as it always was.
 */
export function resolveActiveProduct(input: ActiveProductInput): ActiveProduct {
  const { pathname, stored, ready, enabled, held } = input;
  const forced = productForcedBy(pathname);
  const storedValid = ready && stored !== null && held.includes(stored) ? stored : null;
  if (forced !== null) {
    return {
      product: forced,
      pending: !ready && forced !== DEFAULT_PRODUCT,
      forced,
      storedValid,
      held,
    };
  }
  if (!ready) {
    const waiting = stored !== null && stored !== DEFAULT_PRODUCT;
    return {
      product: waiting ? stored : DEFAULT_PRODUCT,
      pending: waiting,
      forced,
      storedValid,
      held,
    };
  }
  return {
    product: storedValid ?? defaultProductFor(held, enabled),
    pending: false,
    forced,
    storedValid,
    held,
  };
}

/**
 * Whether the shell skips hire's own reads (access scope, the location scope, the engagement flag,
 * SharePoint status): in skyPerformance IT's shell for someone holding no hire role, where the
 * server refuses them (spec 2026-10-26 §4.7, final review M2). Everyone else, skyPerformance's
 * users included, reads exactly as before.
 */
export function skipsHireShellReads(product: ProductKey, holdsHireRole: boolean): boolean {
  return product === "skyperformance_it" && !holdsHireRole;
}
